import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import {
  CountryCallingCode,
  CountryCode,
  getCountryCallingCode,
} from 'libphonenumber-js'
import React, { useMemo } from 'react'
import Dropdown, {
  DropdownItemProps,
  DropdownOption,
  DropdownOptionGroup,
} from 'src/components/Dropdown/Dropdown'
import { LibPhoneNumberCountryCodesSet } from 'src/models/country.models'
import { css } from 'styled-components'
import countries, { Country } from 'world-countries'

export interface CountryPhoneOptionDetails {
  country: Country
  callingCode?: CountryCallingCode
}

export type CountryPhoneOption = DropdownOption<CountryPhoneOptionDetails>

const CountryPhoneDropdownItem: React.FC<
  DropdownItemProps<CountryPhoneOptionDetails>
> = (props): React.ReactElement => {
  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        flex-grow: 2;

        height: 60px;
        ${props.isRoot &&
        css`
          height: 100%;
        `}
      `}
    >
      {props.details !== undefined && (
        <div
          css={css`
            display: flex;
            align-items: center;
          `}
        >
          <span>{props.details.country.flag}</span>
          <span
            css={css`
              ${getFontCss(
                14,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
              margin-left: 6px;
              ${props.isSelected &&
              css`
                ${getFontCss(
                  14,
                  themeConstants.colors.cta,
                  styleConstants.fontFamilies.medium,
                )};
              `}
            `}
          >
            {props.details.country.cca2}
          </span>
          {props.details.callingCode !== undefined && (
            <span
              css={css`
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )};
                margin-left: 6px;
              `}
            >
              {`+${props.details.callingCode}`}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

const countryPhoneOptionFilter = (
  option: CountryPhoneOption,
  val: string,
): boolean =>
  !!option.details?.country.cca2.toLowerCase().includes(val.toLowerCase()) ||
  !!option.details?.country.name.common
    .toLowerCase()
    .includes(val.toLowerCase())

const getCountryPhoneOptionFilterText = (option: CountryPhoneOption): string =>
  option.details?.country.cca2 || ''

interface CountryPhoneDropdownProps {
  className?: string
  selectedValue?: string
  chooseOption: (val: string) => void
  errors?: Array<React.ReactElement | string>
  id?: string
}

const CountryPhoneDropdown: React.FC<CountryPhoneDropdownProps> = (
  props,
): React.ReactElement => {
  const optionGroups: Array<DropdownOptionGroup<CountryPhoneOption>> = useMemo(
    () => [
      {
        options: countries.map((country) => ({
          value: country.cca2,
          details: {
            country,
            callingCode: LibPhoneNumberCountryCodesSet.has(country.cca2)
              ? getCountryCallingCode(country.cca2 as CountryCode)
              : undefined,
          },
        })),
      },
    ],
    [countries],
  )

  return (
    <Dropdown
      id={props.id ? props.id + 'PhoneCode' : 'phoneCode'}
      className={props.className}
      DropdownItem={CountryPhoneDropdownItem}
      optionGroups={optionGroups}
      isMulti={false}
      selectedValues={
        props.selectedValue !== undefined
          ? new Set([props.selectedValue])
          : new Set([])
      }
      chooseOption={props.chooseOption}
      errors={props.errors}
      filter={countryPhoneOptionFilter}
      getFilterText={getCountryPhoneOptionFilterText}
      validateNewOption={undefined}
      countryPhoneDropdown
    />
  )
}

export default CountryPhoneDropdown
